import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Mail, Phone, Quote } from 'lucide-react';

/* ============================================================
   THE ARCHITECTS — Premium semi-circular team arc carousel
   ============================================================ */

const MEMBERS = [
  {
    id: 1, name: 'Vikram Simha Rao', role: 'Founder & CEO',
    email: 'vikramsimharao@galwarcc.in', phone: '+91 77998 56251',
    tagline: 'Building the next era of sci-fi multiplayer combat — from vision to battlefield.',
    vision: 'Cosmic Conquest: Galactic War was born from a simple idea — what if a battle royale was not just about survival, but domination of an entire world? We wanted to replace shrinking circles with living environments, static maps with evolving planets, and generic combat with race-driven warfare.',
  },
  {
    id: 2, name: 'Prashanth Pabbathi', role: 'Co-founder & President',
    photo: '/team/prashanth.jpg',
    email: 'prashanthpabbathi@galwarcc.in', phone: '+91 8886425854',
    tagline: 'Driving strategy, operations, and execution at scale — from planning to launch readiness.',
    vision: 'Cosmic Conquest: Galactic War is built not only on imagination, but on disciplined execution — turning an ambitious universe into a real, scalable product. We focus on aligning the team, timelines, and production pipeline to deliver a competitive sci-fi battle royale experience — with clear milestones, strong processes, and player-first decisions.',
  },
  {
    id: 3, name: 'The Architect', role: 'Lead Developer',
    vision: 'Designs and owns the technical backbone of Cosmic Conquest: Galactic War. Responsible for core gameplay systems, performance optimization, multiplayer logic, and cross-platform scalability — ensuring every mechanic is stable, responsive, and production-ready.',
  },
  {
    id: 4, name: 'The Mechanics', role: 'Game Designer',
    vision: 'Defines how Cosmic Conquest: Galactic War plays, feels, and balances. Designs combat systems, abilities, progression, and gameplay rules — ensuring every encounter is skill-driven, competitive, and strategically meaningful.',
  },
  {
    id: 5, name: 'The World Builder', role: 'Level Designer',
    vision: 'Crafts the battlegrounds where conflict unfolds. Designs map layouts, flow, verticality, and points of interest to drive movement, tension, and tactical decision-making throughout each match.',
  },
  {
    id: 6, name: 'Motion', role: 'Lead Animator',
    vision: 'Brings characters, combat, and traversal to life through movement. Ensures animations feel responsive, impactful, and cinematic — from gunplay and abilities to movement and eliminations.',
  },
  {
    id: 7, name: 'Kinetics', role: 'Senior Rigger',
    vision: 'Builds and maintains the skeletal and control systems behind every animated asset. Ensures characters, equipment, and weapons move naturally while supporting complex combat and animation requirements.',
  },
  {
    id: 8, name: 'Props & Weapons', role: 'Senior Asset Designer',
    vision: 'Creates high-fidelity weapons, gear, and interactive props. Balances visual detail with gameplay readability and performance, ensuring assets look powerful and feel intuitive in combat.',
  },
  {
    id: 9, name: 'Atmosphere', role: 'Environmental Artist',
    vision: 'Shapes the identity and mood of each location. Designs terrain, structures, lighting, and environmental details to make every zone feel alive, hostile, and visually distinct.',
  },
];

// 9 cover-flow slots on a flat baseline. Slot 4 = active (center, largest, facing forward).
const ACTIVE_SLOT = 4;
const SLOTS = [
  { l: 4, ry: -48, s: 0.50, o: 0.28 }, // 0 far-left
  { l: 15, ry: -40, s: 0.62, o: 0.46 }, // 1 mid-left
  { l: 27, ry: -30, s: 0.76, o: 0.66 }, // 2 upper-left
  { l: 39, ry: -18, s: 0.92, o: 0.88 }, // 3 left of center
  { l: 50, ry: 0, s: 1.4, o: 1.0 }, // 4 center (ACTIVE)
  { l: 61, ry: 18, s: 0.92, o: 0.88 }, // 5 right of center
  { l: 73, ry: 30, s: 0.76, o: 0.66 }, // 6 upper-right
  { l: 85, ry: 40, s: 0.62, o: 0.46 }, // 7 mid-right
  { l: 96, ry: 48, s: 0.50, o: 0.28 }, // 8 far-right
];

const CRIMSON = '#FF8618';
const mod = (n, m) => ((n % m) + m) % m;
const initialsOf = (name) =>
  name.replace(/[^a-zA-Z ]/g, '').split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();

// tiny red particles (static, computed once)
const PARTICLES = Array.from({ length: 16 }, () => ({
  left: Math.random() * 100, top: Math.random() * 100,
  size: Math.random() * 2 + 1, delay: Math.random() * 6, dur: Math.random() * 5 + 5,
}));

const PortraitImg = ({ photo, name, className, style }) => {
  const [err, setErr] = useState(false);
  if (!photo || err) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'radial-gradient(circle at 50% 35%, rgba(255,134,24,0.18) 0%, rgba(10,12,14,0.96) 70%)',
        }}
      >
        <span style={{ fontFamily: 'Orbitron, sans-serif', color: CRIMSON, fontWeight: 900, fontSize: '1.6rem', letterSpacing: '0.06em' }}>
          {initialsOf(name)}
        </span>
      </div>
    );
  }
  return (
    <img
      src={photo}
      alt={name}
      className={className}
      style={style}
      loading="lazy"
      onError={() => setErr(true)}
    />
  );
};

const TeamDossier = () => {
  const [rotation, setRotation] = useState(-4);
  const total = MEMBERS.length;
  const activeIndex = mod(rotation + ACTIVE_SLOT, total);
  const active = MEMBERS[activeIndex];

  const arcRef = useRef(null);
  const dragRef = useRef({ x: 0, active: false, fired: false });
  const wheelLock = useRef(false);

  const goNext = useCallback(() => setRotation((r) => r + 1), []);
  const goPrev = useCallback(() => setRotation((r) => r - 1), []);

  const selectMember = useCallback((targetIndex) => {
    setRotation((r) => {
      const curActive = mod(r + ACTIVE_SLOT, total);
      let d = mod(targetIndex - curActive, total);
      if (d > total / 2) d -= total;
      return r + d;
    });
  }, [total]);

  // Wheel-to-rotate (scoped to the arc, throttled)
  useEffect(() => {
    const el = arcRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) < 6 && Math.abs(e.deltaX) < 6) return;
      e.preventDefault();
      if (wheelLock.current) return;
      wheelLock.current = true;
      const dir = e.deltaY + e.deltaX > 0 ? 1 : -1;
      setRotation((r) => r + dir);
      setTimeout(() => { wheelLock.current = false; }, 320);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e) => { dragRef.current = { x: e.clientX, active: true, fired: false }; };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active || d.fired) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 22) {
      setRotation((r) => r + (dx < 0 ? 1 : -1));
      d.fired = true;
    }
  };
  const onPointerUp = () => { dragRef.current.active = false; };

  return (
    <section
      id="team"
      data-testid="team-section"
      className="gw-section"
      style={{
        paddingTop: '2.25rem', paddingBottom: '2.25rem',
      }}
    >
      {/* Ambient warm backdrop */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 16%, rgba(255,134,24,0.06) 0%, transparent 60%), radial-gradient(ellipse 60% 55% at 50% 92%, rgba(208,158,98,0.08) 0%, transparent 60%)',
        }}
      />
      {/* Red digital particles */}
      <div className="absolute inset-0 pointer-events-none">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="absolute rounded-full team-particle"
            style={{
              left: `${p.left}%`, top: `${p.top}%`,
              width: p.size, height: p.size, background: CRIMSON,
              boxShadow: `0 0 6px ${CRIMSON}`, opacity: 0.5,
              animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`,
            }}
          />
        ))}
      </div>

      <div className="gw-section-inner">
        {/* ===== HEADER ===== */}
        <div className="text-center mb-4 md:mb-3">
          <div className="flex items-center justify-center gap-3 mb-1.5">
            <span className="block h-px w-8 sm:w-12" style={{ background: 'linear-gradient(90deg, transparent, #FF8618)' }} />
            <span
              className="text-[9px] sm:text-[10px] font-bold uppercase"
              style={{ fontFamily: 'Rajdhani, sans-serif', color: CRIMSON, letterSpacing: '0.36em' }}
            >
              Development Team
            </span>
            <span className="block h-px w-8 sm:w-12" style={{ background: 'linear-gradient(90deg, #FF8618, transparent)' }} />
          </div>
          <h2
            className="font-black uppercase leading-[0.9]"
            style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(1.35rem, 2.8vw, 2.3rem)', fontStyle: 'normal', letterSpacing: '0.02em' }}
            data-testid="team-title"
          >
            <span style={{ color: 'var(--text-primary)', textShadow: '0 1px 0 rgba(255,255,255,0.6)' }}>THE </span>
            <span style={{ color: CRIMSON, textShadow: '0 0 28px rgba(255,134,24,0.45)' }}>ARCHITECTS</span>
          </h2>
          <p
            className="mt-1.5 text-[11px]"
            style={{ fontFamily: 'Rajdhani, sans-serif', color: 'var(--text-secondary)', letterSpacing: '0.06em' }}
          >
            The minds shaping Cosmic Conquest: Galactic War.
          </p>
        </div>

        {/* ===== DESKTOP / TABLET ARC ===== */}
        <div className="relative hidden md:block mt-8 lg:mt-10">
          {/* Prev / Next arrows */}
          <button
            onClick={goPrev}
            data-testid="team-prev-btn"
            aria-label="Previous member"
            className="team-arrow absolute left-0 lg:left-2 top-[42%] -translate-y-1/2 z-30"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={goNext}
            data-testid="team-next-btn"
            aria-label="Next member"
            className="team-arrow absolute right-0 lg:right-2 top-[42%] -translate-y-1/2 z-30"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Arc stage */}
          <div
            ref={arcRef}
            data-testid="team-arc"
            className="relative mx-auto select-none"
            style={{ height: 'clamp(260px, 28vw, 370px)', maxWidth: '1100px', touchAction: 'pan-y', perspective: '1400px' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
          >
            {/* Cover-flow cards — center card doubles as the selected-member hero */}
            {MEMBERS.map((m, i) => {
              const s = mod(i - rotation, total);
              const slot = SLOTS[s];
              const isActive = s === ACTIVE_SLOT;
              return (
                <button
                  key={m.id}
                  onClick={() => selectMember(i)}
                  data-testid={`team-card-${m.id}`}
                  className="absolute focus:outline-none"
                  style={{
                    left: `${slot.l}%`,
                    top: '50%',
                    width: 'clamp(92px, 10.5vw, 140px)',
                    transform: `translate(-50%, -50%) rotateY(${slot.ry}deg) scale(${slot.s})`,
                    opacity: slot.o,
                    zIndex: 20 - Math.abs(s - ACTIVE_SLOT),
                    transition: 'left 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1), opacity 0.5s ease',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="relative w-full overflow-hidden"
                    style={{
                      aspectRatio: '3 / 4',
                      borderRadius: '16px',
                      border: isActive ? `2px solid ${CRIMSON}` : '1px solid rgba(120,138,152,0.35)',
                      boxShadow: isActive
                        ? `0 0 0 4px rgba(255,134,24,0.12), 0 0 34px rgba(255,134,24,0.4), 0 14px 30px rgba(0,0,0,0.5)`
                        : '0 8px 18px rgba(0,0,0,0.35)',
                      background: '#14181D',
                    }}
                    data-testid={isActive ? 'team-hero-portrait' : undefined}
                  >
                    <PortraitImg
                      photo={m.photo}
                      name={m.name}
                      className="w-full h-full"
                      style={{ objectFit: 'cover', objectPosition: 'center top' }}
                    />
                    <div
                      className="absolute inset-0 pointer-events-none"
                      style={{ background: 'linear-gradient(to bottom, transparent 45%, rgba(20,24,29,0.75) 100%)' }}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ===== MOBILE ===== */}
        <div className="md:hidden">
          <div className="relative mx-auto mt-2 overflow-hidden" style={{ width: '195px', height: '255px', borderRadius: '16px', border: `2px solid ${CRIMSON}`, boxShadow: `0 0 30px rgba(255,134,24,0.35), 0 14px 30px rgba(0,0,0,0.5)` }} data-testid="team-hero-portrait-mobile">
            <PortraitImg photo={active.photo} name={active.name} className="relative w-full h-full" style={{ objectFit: 'cover', objectPosition: 'center top' }} />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent 45%, rgba(20,24,29,0.75) 100%)' }} />
          </div>
          {/* thumb strip */}
          <div className="mt-4 overflow-x-auto whitespace-nowrap pb-2 team-thumb-strip" data-testid="team-thumb-strip">
            <div className="inline-flex gap-3 px-2">
              {MEMBERS.map((m, i) => {
                const isActive = i === activeIndex;
                return (
                  <button
                    key={m.id}
                    onClick={() => selectMember(i)}
                    data-testid={`team-thumb-${m.id}`}
                    className="flex-shrink-0 overflow-hidden"
                    style={{
                      width: '62px', height: '80px', borderRadius: '12px',
                      border: isActive ? `2px solid ${CRIMSON}` : '1px solid rgba(255,255,255,0.12)',
                      boxShadow: isActive ? '0 0 18px rgba(255,134,24,0.4)' : '0 6px 16px rgba(0,0,0,0.3)',
                    }}
                  >
                    <PortraitImg photo={m.photo} name={m.name} className="w-full h-full" style={{ objectFit: 'cover', objectPosition: 'center top' }} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* ===== ACTIVE MEMBER DETAILS ===== */}
        <div className="text-center mt-4 md:mt-2.5 relative z-20">
          <h3
            className="font-bold"
            style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3.2vw, 2.3rem)', color: 'var(--text-primary)', letterSpacing: '0.01em' }}
            data-testid="active-member-name"
          >
            {active.name}
          </h3>
          <div className="flex items-center justify-center gap-3 mt-1">
            <span className="block h-px w-6" style={{ background: 'rgba(255,134,24,0.5)' }} />
            <p
              className="font-bold uppercase"
              style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: 'clamp(0.8rem, 1.4vw, 1rem)', color: CRIMSON, letterSpacing: '0.16em' }}
              data-testid="active-member-role"
            >
              {active.role}
            </p>
            <span className="block h-px w-6" style={{ background: 'rgba(255,134,24,0.5)' }} />
          </div>

          {/* Tagline (leaders only) */}
          {active.tagline && (
            <p
              className="mt-3 max-w-2xl mx-auto"
              style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 500, fontSize: 'clamp(0.85rem, 1.3vw, 1rem)', fontStyle: 'normal', color: '#BFC8D0', letterSpacing: '0.02em' }}
              data-testid="active-member-tagline"
            >
              {active.tagline}
            </p>
          )}

          {/* Contact row (only if contact info exists) */}
          {(active.email || active.phone) && (
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-3.5">
              {active.email && (
                <a href={`mailto:${active.email}`} data-testid="contact-email" className="team-contact-item flex items-center gap-2">
                  <Mail className="w-4 h-4" style={{ color: CRIMSON }} />
                  <span>{active.email}</span>
                </a>
              )}
              {active.email && active.phone && (
                <span className="hidden sm:block w-px h-4" style={{ background: 'rgba(120,138,152,0.4)' }} />
              )}
              {active.phone && (
                <a href={`tel:${active.phone.replace(/\s/g, '')}`} data-testid="contact-phone" className="team-contact-item flex items-center gap-2">
                  <Phone className="w-4 h-4" style={{ color: CRIMSON }} />
                  <span>{active.phone}</span>
                </a>
              )}
            </div>
          )}

          {/* THE VISION — wide horizontal panel below the cards */}
          <div
            className="relative max-w-4xl mx-auto mt-6 px-6 sm:px-10 py-6 sm:py-7"
            style={{
              borderRadius: '28px',
              background: 'linear-gradient(135deg, rgba(16,22,28,0.78) 0%, rgba(22,29,36,0.65) 100%)',
              border: '1px solid rgba(255,134,24,0.22)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 20px 50px rgba(0,0,0,0.5)',
              backdropFilter: 'blur(16px) saturate(140%)',
              WebkitBackdropFilter: 'blur(16px) saturate(140%)',
            }}
            data-testid="team-vision-panel"
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <span className="block h-px w-12 sm:w-20" style={{ background: 'linear-gradient(90deg, transparent, #FF8618)' }} />
              <span
                className="font-bold uppercase"
                style={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '12px', color: CRIMSON, letterSpacing: '0.4em' }}
              >
                The Vision
              </span>
              <span className="block h-px w-12 sm:w-20" style={{ background: 'linear-gradient(90deg, #FF8618, transparent)' }} />
            </div>
            <div className="relative px-7">
              <Quote className="absolute -left-1 sm:left-0 -top-1 w-6 h-6" style={{ color: CRIMSON, opacity: 0.9 }} fill={CRIMSON} />
              <p
                className="leading-relaxed text-center"
                style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 500, fontSize: 'clamp(0.95rem, 1.7vw, 1.3rem)', color: 'var(--text-secondary)' }}
                data-testid="team-vision-quote"
              >
                {active.vision}
              </p>
              <Quote className="absolute -right-1 sm:right-0 -bottom-1 w-6 h-6 rotate-180" style={{ color: CRIMSON, opacity: 0.9 }} fill={CRIMSON} />
            </div>
          </div>
        </div>

        {/* ===== DRAG / SCROLL HINT ===== */}
        <div className="flex items-center justify-center gap-3 mt-4" data-testid="team-scroll-hint">
          <svg width="20" height="30" viewBox="0 0 20 30" fill="none">
            <rect x="1" y="1" width="18" height="28" rx="9" stroke="#8A939C" strokeWidth="1.5" />
            <circle className="team-mouse-dot" cx="10" cy="8" r="2" fill={CRIMSON} />
          </svg>
          <span
            className="uppercase"
            style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, fontSize: '11px', color: 'var(--text-muted)', letterSpacing: '0.28em' }}
          >
            Drag / Scroll to rotate
          </span>
        </div>
      </div>
    </section>
  );
};

export default TeamDossier;
